import { Box, Stack } from '@mui/material';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';

import LeftArrow from '@/assets/svg/ui-elements/chevron-left.svg';
import RightArrow from '@/assets/svg/ui-elements/chevron-right.svg';
import { IconButton } from '@/design-system';

import { CarouselBarCard } from '../../analytics-tab/carousel-bar-card';
import { CarouselLineCard } from '../../analytics-tab/carousel-line-card';
import { CardProps } from '../../analytics-tab/common';
import { SkeletonBox } from '../styled';
import { CarouselRow, CarouselViewport, SkeletonCard } from './charts-carousel.styled';

const CARDS_GAP = 16;
const CARD_MIN_WIDTH = 352;
const MAX_CARDS_PER_SLIDE = 3;

const ChartCardSkeleton = () => (
  <SkeletonCard>
    <SkeletonBox animation="wave" height={23} width={180} sx={{ borderRadius: '8px' }} />
    <SkeletonBox
      animation="wave"
      height={23}
      width={180}
      sx={{ mt: '12px', borderRadius: '8px' }}
    />
    <SkeletonBox animation="wave" height={179} sx={{ mt: '16px', borderRadius: '12px' }} />
  </SkeletonCard>
);

/**
 * Cards have a fixed design width, so how many fit is a question about the
 * viewport element rather than the window — the sidebar and page padding both
 * eat into it.
 */
const useCardsPerSlide = (viewportRef: RefObject<HTMLDivElement | null>) => {
  const [cardsPerSlide, setCardsPerSlide] = useState(MAX_CARDS_PER_SLIDE);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      const fits = Math.floor((width + CARDS_GAP) / (CARD_MIN_WIDTH + CARDS_GAP));

      setCardsPerSlide(Math.min(MAX_CARDS_PER_SLIDE, Math.max(1, fits)));
    });

    observer.observe(viewport);

    return () => observer.disconnect();
  }, [viewportRef]);

  return cardsPerSlide;
};

const useSlides = (cards: CardProps[], cardsPerSlide: number) => {
  const slides = useMemo(() => {
    const out: CardProps[][] = [];
    for (let i = 0; i < cards.length; i += cardsPerSlide) {
      out.push(cards.slice(i, i + cardsPerSlide));
    }
    return out;
  }, [cards, cardsPerSlide]);

  return { slides, slidesCount: Math.max(slides.length, 1) };
};

export const ChartsCarouselFallback = ({ cards }: { cards: CardProps[] }) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const cardsPerSlide = useCardsPerSlide(viewportRef);
  const { slides, slidesCount } = useSlides(cards, cardsPerSlide);

  return (
    <CarouselRow>
      <IconButton size="xs" variant="solid" color="tertiary" disabled aria-label="Previous slide">
        <LeftArrow width={24} height={24} />
      </IconButton>

      <CarouselViewport ref={viewportRef}>
        <Stack direction="row" sx={{ width: `${slidesCount * 100}%` }}>
          {slides.map((group, idx) => (
            <Box key={idx} sx={{ width: `${100 / slidesCount}%` }}>
              <Box
                display="grid"
                gridTemplateColumns={`repeat(${cardsPerSlide}, 1fr)`}
                gap={`${CARDS_GAP}px`}
              >
                {group.map((_, i) => (
                  <ChartCardSkeleton key={i} />
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      </CarouselViewport>

      <IconButton size="xs" variant="solid" color="tertiary" disabled aria-label="Next slide">
        <RightArrow width={24} height={24} />
      </IconButton>
    </CarouselRow>
  );
};

export const ChartsCarousel = ({ cards }: { cards: CardProps[] }) => {
  const [current, setCurrent] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const cardsPerSlide = useCardsPerSlide(viewportRef);
  const { slides, slidesCount } = useSlides(cards, cardsPerSlide);

  const safeCurrent = Math.min(current, slidesCount - 1);
  const canPrev = safeCurrent > 0;
  const canNext = safeCurrent < slidesCount - 1;

  return (
    <CarouselRow>
      <IconButton
        size="xs"
        variant="solid"
        color="tertiary"
        disabled={!canPrev}
        onClick={() => setCurrent((v) => Math.max(0, v - 1))}
        aria-label="Previous slide"
      >
        <LeftArrow width={24} height={24} />
      </IconButton>

      <CarouselViewport ref={viewportRef}>
        <Stack
          sx={{
            width: `${slidesCount * 100}%`,
            transform: `translateX(-${safeCurrent * (100 / slidesCount)}%)`,
            transition: 'transform 0.5s ease',
          }}
        >
          {slides?.map((group, idx) => (
            <Box key={idx} sx={{ width: `${100 / slidesCount}%` }}>
              <Box
                display="grid"
                gridTemplateColumns={`repeat(${cardsPerSlide}, 1fr)`}
                gap={`${CARDS_GAP}px`}
              >
                {group?.map((card, index) =>
                  card?.type === 'bar' ? (
                    <CarouselBarCard key={index} card={card} />
                  ) : (
                    <CarouselLineCard key={index} card={card} />
                  ),
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      </CarouselViewport>

      <IconButton
        size="xs"
        variant="solid"
        color="tertiary"
        disabled={!canNext}
        onClick={() => setCurrent((v) => Math.min(slidesCount - 1, v + 1))}
        aria-label="Next slide"
      >
        <RightArrow width={24} height={24} />
      </IconButton>
    </CarouselRow>
  );
};
